import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import {
  GameContainer,
  GameCard,
  GameContent,
  GameTitle,
  GameSubtitle,
  GameStatusDisplay,
  NewGameButton,
  GuessesCounter,
} from './styles.js';
import { GAME_CONFIG } from './GameConfig.js';
import { initializeGame, processGuess } from './gameLogic.js';
import { sections } from './words.js';
import StickFigure from './StickFigure.js';
import WordDisplay from './WordDisplay.js';
import Keyboard from './Keyboard.js';
import {
  trackHangmanGameStart,
  trackHangmanGameEnd,
  trackHangmanCategoryChange,
} from '../../../../utils/analytics';

const Hangman = () => {
  const [gameState, setGameState] = useState(initializeGame());
  const [selectedColorScheme] = useState('celebrate');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [gameKey, setGameKey] = useState(0); // Key to force StickFigure re-mount
  const gameContainerRef = useRef(null);
  const wordDisplayRef = useRef(null);

  const handleLetterClick = useCallback(
    letter => {
      if (gameState.gameStatus === 'playing') {
        const newGameState = processGuess(letter, gameState);
        setGameState(newGameState);
      }
    },
    [gameState]
  );

  // Handle keyboard input for letter guessing
  useEffect(() => {
    const handleKeyDown = event => {
      // Only process if game is playing and key is a letter
      if (gameState.gameStatus !== 'playing') {
        return;
      }

      const key = event.key.toUpperCase();
      // Check if it's a letter A-Z
      if (key.length === 1 && key >= 'A' && key <= 'Z') {
        // Prevent default to avoid typing in input fields
        if (
          document.activeElement.tagName !== 'INPUT' &&
          document.activeElement.tagName !== 'TEXTAREA' &&
          document.activeElement.tagName !== 'SELECT'
        ) {
          event.preventDefault();
          handleLetterClick(key);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [gameState.gameStatus, handleLetterClick]);

  // Track initial game load
  useEffect(() => {
    trackHangmanGameStart(selectedCategory);
  }, [selectedCategory]); // Run when selectedCategory changes

  // Track game end events
  useEffect(() => {
    if (gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') {
      trackHangmanGameEnd(
        gameState.gameStatus,
        selectedCategory,
        gameState.wrongGuesses
      );
      // Move focus to status message when game ends
      setTimeout(() => {
        const statusElement = document.querySelector('[role="status"]');
        if (statusElement) {
          statusElement.focus();
        }
      }, 100);
    }
  }, [gameState.gameStatus, selectedCategory, gameState.wrongGuesses]);

  const handleNewGame = () => {
    setGameState(initializeGame(selectedCategory));
    setGameKey(prev => prev + 1); // Force StickFigure to re-mount
    trackHangmanGameStart(selectedCategory);
    // Return focus to game area after starting new game
    setTimeout(() => {
      if (wordDisplayRef.current) {
        wordDisplayRef.current.focus();
      }
    }, 100);
  };

  const getStatusMessage = () => {
    switch (gameState.gameStatus) {
      case 'won':
        return GAME_CONFIG.WIN_MESSAGE;
      case 'lost':
        return `${GAME_CONFIG.LOSE_MESSAGE}${gameState.word}`;
      default:
        return `Guesses remaining: ${GAME_CONFIG.MAX_GUESSES - gameState.wrongGuesses}`;
    }
  };

  const getButtonText = () => {
    return gameState.gameStatus === 'playing'
      ? GAME_CONFIG.NEW_GAME_TEXT
      : GAME_CONFIG.PLAY_AGAIN_TEXT;
  };

  return (
    <GameContainer
      ref={gameContainerRef}
      role="region"
      aria-label="Hangman word guessing game"
    >
      <GameCard>
        <GameContent>
          <GameTitle variant="h1" component="h1">
            {GAME_CONFIG.GAME_TITLE}
          </GameTitle>
          <GameSubtitle variant="subtitle1" component="h2">
            {GAME_CONFIG.GAME_SUBTITLE}
          </GameSubtitle>
          <GameSubtitle
            variant="caption"
            sx={{ mt: 1, mb: 2, fontStyle: 'italic' }}
          >
            Tip: You can type letters on your keyboard to guess, or click the
            letter buttons below.
          </GameSubtitle>

          <div>
            <FormControl
              size="small"
              sx={{
                minWidth: 200,
                '@media (max-width: 600px)': {
                  minWidth: 150,
                },
                '@media (max-width: 480px)': {
                  minWidth: 120,
                },
              }}
            >
              <InputLabel id="category-label" htmlFor="category-select">
                Word Category
              </InputLabel>
              <Select
                labelId="category-label"
                id="category-select"
                value={selectedCategory}
                label="Word Category"
                name="word-category"
                inputProps={{
                  'aria-labelledby': 'category-label',
                }}
                onChange={e => {
                  const newCategory = e.target.value;
                  setSelectedCategory(newCategory);
                  // Start new game with selected category
                  setGameState(initializeGame(newCategory));
                  setGameKey(prev => prev + 1);
                  trackHangmanCategoryChange(newCategory);
                }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {sections.map(section => (
                  <MenuItem key={section} value={section}>
                    {section}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <StickFigure
            key={gameKey}
            wrongGuesses={gameState.wrongGuesses}
            maxGuesses={GAME_CONFIG.MAX_GUESSES}
            gameStatus={gameState.gameStatus}
            selectedColorScheme={selectedColorScheme}
          />

          <WordDisplay
            ref={wordDisplayRef}
            word={gameState.word}
            guessedLetters={gameState.guessedLetters}
          />

          <GameStatusDisplay
            status={gameState.gameStatus}
            role="status"
            aria-live="polite"
            aria-atomic="true"
            tabIndex="-1"
          >
            {getStatusMessage()}
          </GameStatusDisplay>

          <GuessesCounter variant="body2">
            Wrong guesses: {gameState.wrongGuesses}/{GAME_CONFIG.MAX_GUESSES}
          </GuessesCounter>

          <Keyboard
            guessedLetters={gameState.guessedLetters}
            onLetterClick={handleLetterClick}
            gameStatus={gameState.gameStatus}
            word={gameState.word}
          />

          <NewGameButton
            onClick={handleNewGame}
            variant="contained"
            size="large"
          >
            {getButtonText()}
          </NewGameButton>
        </GameContent>
      </GameCard>
    </GameContainer>
  );
};

export default Hangman;
